import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Layout } from '../components/layout';
import { UserPlus, Shield } from 'lucide-react';

const LABEL = 'block font-mono text-xs text-gray-400 mb-2 uppercase tracking-wider';
const INPUT = 'w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors';

const Register = () => {
  const [firstName, setFirstName]             = useState('');
  const [lastName, setLastName]               = useState('');
  const [postalCode, setPostalCode]           = useState('');
  const [email, setEmail]                     = useState('');
  const [username, setUsername]               = useState('');
  const [usernameError, setUsernameError]     = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [petitionConsent, setPetitionConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [livesInCondo, setLivesInCondo] = useState(false);
  const [loading, setLoading]                 = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateUsername = (val) => {
    if (val.length < 3) return 'At least 3 characters required';
    if (val.length > 30) return 'Maximum 30 characters';
    if (!/^[a-zA-Z0-9_-]+$/.test(val)) return 'Only letters, numbers, _ and - allowed';
    return '';
  };

  const handleUsernameChange = (e) => {
    const val = e.target.value;
    setUsername(val);
    setUsernameError(val ? validateUsername(val) : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const uErr = validateUsername(username);
    if (uErr) { toast.error(uErr); return; }

    const postalRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    if (!postalRegex.test(postalCode)) {
      toast.error('Please enter a valid Canadian postal code');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      await register(fullName, username, postalCode.toUpperCase(), email, password, petitionConsent, marketingConsent, livesInCondo);
      toast.success('Account created! You can now log in.');
      if (livesInCondo) {
        navigate('/condo-welcome');
      } else {
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-headline text-4xl text-white mb-2">REGISTER</h1>
            <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
              Verify Your Residency
            </p>
          </div>

          <div className="bg-[#111] border border-[#1a1a1a] p-8 rounded">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* First + Last Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className={LABEL}>First Name</label>
                  <input id="firstName" type="text" value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required className={INPUT} placeholder="Jane"
                    data-testid="register-firstname" />
                </div>
                <div>
                  <label htmlFor="lastName" className={LABEL}>Last Name</label>
                  <input id="lastName" type="text" value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required className={INPUT} placeholder="Doe"
                    data-testid="register-lastname" />
                </div>
              </div>

              {/* Postal Code */}
              <div>
                <label htmlFor="postalCode" className={LABEL}>Postal Code</label>
                <input id="postalCode" type="text" value={postalCode}
                  onChange={e => setPostalCode(e.target.value.toUpperCase())}
                  required maxLength="7" className={INPUT} placeholder="S4P 3Y2"
                  data-testid="register-postal" />
              </div>

              {/* Condo toggle */}
              <label className="flex items-center gap-3 cursor-pointer bg-[#0a0a0a] border border-[#2a2a2a] rounded px-4 py-3 hover:border-[#C5A52A]/40 transition-colors">
                <div
                  onClick={() => setLivesInCondo(v => !v)}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${livesInCondo ? 'bg-[#C5A52A]' : 'bg-[#2a2a2a]'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${livesInCondo ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="font-mono text-xs text-gray-300">I live in a condo</span>
              </label>

              {/* Privacy notice */}
              <div className="flex items-start gap-2 p-3 bg-[#0a0a0a] border border-green-900/40 rounded -mt-1">
                <Shield className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="font-mono text-[10px] text-gray-400 leading-relaxed">
                  Your postal code is utilized to ensure that you are interacting with your community
                  and proper city councillor. Please ensure it is accurate.
                </p>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className={LABEL}>Email Address</label>
                <input id="email" type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  required className={INPUT} placeholder="your@email.com"
                  data-testid="register-email" />
                <p className="font-mono text-[10px] text-gray-600 mt-1">
                  Never shared publicly or with councillors.
                </p>
              </div>

              {/* Username */}
              <div>
                <label htmlFor="username" className={LABEL}>Username</label>
                <input id="username" type="text" value={username}
                  onChange={handleUsernameChange}
                  required minLength="3" maxLength="30"
                  className={`${INPUT} ${usernameError ? 'border-red-600 focus:border-red-500' : ''}`}
                  placeholder="your_username"
                  data-testid="register-username" />
                {usernameError
                  ? <p className="font-mono text-[10px] text-red-400 mt-1">{usernameError}</p>
                  : <p className="font-mono text-[10px] text-gray-600 mt-1">Displayed publicly. Letters, numbers, _ and - only.</p>
                }
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className={LABEL}>Password</label>
                <input id="password" type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  required minLength="6" className={INPUT} placeholder="••••••••"
                  data-testid="register-password" />
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className={LABEL}>Confirm Password</label>
                <input id="confirmPassword" type="password" value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required minLength="6" className={INPUT} placeholder="••••••••"
                  data-testid="register-confirm" />
              </div>

              {/* Optional consent checkboxes */}
              <div className="space-y-4 pt-1">

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={petitionConsent}
                    onChange={e => setPetitionConsent(e.target.checked)}
                    className="mt-0.5 flex-shrink-0 accent-[#C5A52A] w-4 h-4"
                    data-testid="register-petition" />
                  <span className="font-mono text-[11px] text-gray-400 leading-relaxed">
                    I am willing to sign an official Petition (Request for Referendum) for important matters
                    and I consent to email correspondence regarding the same.{' '}
                    <span className="text-gray-600 text-[10px]">
                      (Optional — Checking this only consents to being informed via email. Your physical
                      signature would be required separately to officially sign any Petition.)
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={marketingConsent}
                    onChange={e => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 flex-shrink-0 accent-[#C5A52A] w-4 h-4"
                    data-testid="register-marketing" />
                  <span className="font-mono text-[11px] text-gray-400 leading-relaxed">
                    I consent to receiving product updates, newsletters, and promotional offers
                    from cYQR.ca. <span className="text-gray-600">(rare)</span>
                  </span>
                </label>

                <p className="font-mono text-[10px] text-gray-600 pl-7 leading-relaxed">
                  I understand that I can unsubscribe from the above noted email communications at any time.
                </p>

              </div>

              {/* Terms notice above button */}
              <p className="font-mono text-[10px] text-gray-500 text-center leading-relaxed">
                By clicking "Create Account", you agree to our{' '}
                <Link to="/terms" target="_blank" className="text-gray-400 hover:text-[#C5A52A] transition-colors underline">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/terms" target="_blank" className="text-gray-400 hover:text-[#C5A52A] transition-colors underline">
                  Privacy Policy
                </Link>.
              </p>

              <button
                type="submit"
                disabled={loading || !!usernameError}
                className="w-full bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/40 disabled:cursor-not-allowed text-black font-display text-sm py-3 tracking-wider transition-colors flex items-center justify-center gap-2"
                data-testid="register-submit"
              >
                <UserPlus className="w-4 h-4" />
                {loading ? 'REGISTERING...' : 'CREATE ACCOUNT'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#1a1a1a] text-center">
              <p className="font-mono text-xs text-gray-500">
                Already registered?{' '}
                <Link to="/login" className="text-[#C5A52A] hover:text-[#D4B93A] transition-colors">
                  LOGIN
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
